/* eslint-disable @typescript-eslint/naming-convention */
import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
} from "@angular/core";

import {
    combineLatest,
    map,
    merge,
    Observable,
    pairwise,
    scan,
    startWith,
    Subject,
} from "rxjs";

import { Asset } from "@hermes/api-model-asset";
import { Product } from "@hermes/api-model-product";

import { PRODUCT_PAGE_IMAGE_SIZE } from "@hermes/fragments/product-utils";
import { ProductImageSeoUrlPipe } from "@hermes/states/product-page";
import {
    AssetSourceValue,
    DOWN,
    LEFT,
    RIGHT,
    UP,
} from "@hermes/utils-generic/constants";
import {
    PanEvent,
    PinchEvent,
} from "@hermes/utils-generic/directives/gestures";

import { ProductImagePersoUrlPipe } from "../../pipes/product-image-perso-url.pipe";
import { ZoomService } from "../../services/zoom.service";

import {
    Coordinate,
    DeltaCoordinate,
    CursorDeltaCoordinate,
    Transform,
    TransformState,
} from "../../types/zoom.types";

// Step size for key press
const MOVE_STEP = 10;

/**
 * this component allows to zoom an image from a scale
 * it also manages the navigation event drag and drop (pan)
 */
@Component({
    selector: "h-product-image-zoom-image",
    templateUrl: "./product-image-zoom-image.component.html",
    styleUrls: ["./product-image-zoom-image.component.scss"],
})
export class ProductImageZoomImageComponent implements OnChanges {
    @Input({ required: true })
    public product!: Product;

    /**
     * current asset
     */
    @Input({ required: true })
    public asset!: Asset;

    @Input()
    public backgroundWhite: boolean = false;

    @Input() public imageIndex: number = 0;

    @ViewChild("container")
    private containerRef!: ElementRef<HTMLDivElement>;

    public readonly minScale = ZoomService.minScale;
    public readonly maxScale = ZoomService.maxScale;

    /**
     * true when low quality image is loaded
     */
    public highQualityLoaded: boolean = false;
    public lowQualityUrl: string | undefined;
    public highQualityUrl: string | undefined;
    public transform$: Observable<Transform>;
    public tabIndex$: Observable<string>;
    public cursor$: Observable<string>;

    private panFromkeyboard = new Subject<string>();
    private panFromCursor = new Subject<CursorDeltaCoordinate>();
    private initialCoordinate: Coordinate = { x: 0, y: 0 };
    private noDelta: DeltaCoordinate = { deltaX: 0, deltaY: 0 };

    constructor(
        private productImageSeoUrlPipe: ProductImageSeoUrlPipe,
        private productImagePersoUrlPipe: ProductImagePersoUrlPipe,
        private readonly zoomService: ZoomService,
    ) {
        const scale$ = this.zoomService.scale$;

        this.tabIndex$ = scale$.pipe(
            map((scale) => (this.zoomService.isMinScale(scale) ? "-1" : "0")),
        );
        this.cursor$ = scale$.pipe(
            map((scale) =>
                this.zoomService.isMinScale(scale) ? "default" : "grab",
            ),
        );

        const keyboardDelta$ = this.panFromkeyboard.pipe(
            map((key) => this.getAxisDeltaByKey(key)),
        );

        const cursorDelta$ = this.panFromCursor.pipe(
            pairwise(),
            map(([n, n1]) => this.getAxisDeltaByPanDelta(n, n1)),
        );

        const delta$ = merge(cursorDelta$, keyboardDelta$).pipe(
            startWith(this.noDelta),
        );

        this.transform$ = combineLatest([delta$, scale$]).pipe(
            scan(({ coordinate }, [axisDelta, scale]) => {
                if (this.zoomService.isMinScale(scale)) {
                    return this.setTransforms();
                }
                const position = this.panImage(coordinate, axisDelta);

                return this.setTransforms(position, scale);
            }, this.setTransforms()),
            map((state) => state.transform),
        );
    }

    public ngOnChanges({ asset }: SimpleChanges): void {
        if (asset.currentValue) {
            const assetValue = asset.currentValue as unknown as Asset;
            this.zoomService.resetScale();

            this.highQualityLoaded = false;
            this.lowQualityUrl = this.getImageSrc(assetValue, "low");
            this.highQualityUrl = this.getImageSrc(assetValue, "high");
        }
    }

    public onPinchMove(event: PinchEvent) {
        this.zoomService.pinch(event);
    }
    public onPanMove(event: PanEvent) {
        this.panFromCursor.next(event);
    }

    public panByKeyDown({ key }: KeyboardEvent): void {
        this.panFromkeyboard.next(key);
    }

    public handleHighQualityLoaded({ target: image }: Event): void {
        this.highQualityLoaded = true;
        this.zoomService.scaleForHighQuality(image as HTMLImageElement);
    }

    public onAssetLoadError() {
        this.highQualityLoaded = true;
    }

    /**
     * Transform url based on product templateType
     */
    private getImageSrc(
        { source, url }: Asset,
        quality: "low" | "high",
    ): string | undefined {
        if (source === AssetSourceValue.Platforme) {
            return this.productImagePersoUrlPipe.transform(url, 1200);
        }

        const urlOptions = {
            slug: this.product.slug,
            imageSize: PRODUCT_PAGE_IMAGE_SIZE * 2,
            quality: quality === "low" ? 50 : 99,
            templateType: this.product.templateType,
        };

        return this.productImageSeoUrlPipe.transform(url, urlOptions);
    }

    private setTransforms(
        coordinate?: Coordinate,
        scale?: number,
    ): TransformState {
        const { x, y } = coordinate || this.initialCoordinate;
        scale = scale || ZoomService.minScale;

        return {
            coordinate: { x, y },
            transform: `translate(${x}px, ${y}px) scale(${scale})`,
        };
    }

    private getAxisDeltaByKey(key: string): DeltaCoordinate {
        switch (key) {
            case LEFT: {
                return this.deltaXY(-MOVE_STEP)(0);
            }
            case RIGHT: {
                return this.deltaXY(MOVE_STEP)(0);
            }
            case UP: {
                return this.deltaXY(0)(-MOVE_STEP);
            }
            case DOWN: {
                return this.deltaXY(0)(MOVE_STEP);
            }
            default: {
                return this.noDelta;
            }
        }
    }

    private getAxisDeltaByPanDelta(
        previous: CursorDeltaCoordinate,
        current: CursorDeltaCoordinate,
    ) {
        if (previous.isFinal) {
            return this.noDelta;
        }
        const x = current.deltaX - previous.deltaX;
        const y = current.deltaY - previous.deltaY;

        return this.deltaXY(x)(y);
    }

    /**
     * Pan image and keep it visible at 30%
     */
    private panImage(position: Coordinate, pan: DeltaCoordinate): Coordinate {
        position.x += pan.deltaX;
        position.y += pan.deltaY;
        const boundary = this.containerRef.nativeElement as HTMLElement;

        return this.zoomService.restrictPosition(position, boundary);
    }

    private deltaXY =
        (x: number = 0) =>
        (y: number = 0) => ({
            deltaX: x,
            deltaY: y,
        });
}
